/**
 * Module 8 — Cuelist-side structure-sync access helpers (frontend).
 * When VUE_APP_NOTESLIST_INTEGRATION_BILLING_ENFORCED is false, access checks
 * always pass so alpha sync stays unchanged.
 * Account/project linking is free; this gate is for Re-sync / live structure sync.
 *
 * Prefer the server proxy GET /integrations/noteslist/sync-access for the
 * both-sides licence check; this helper only mirrors the local dual flag.
 */
import {
    NOTESLIST_INTEGRATION_BILLING_ENFORCED,
    NOTESLIST_SYNC_ACCESS_URL,
    NOTESLIST_BUNDLE_ELIGIBILITY_URL,
    NOTESLIST_LINK_ACCESS_URL,
} from '@/lib/URLs';
import firebase from 'firebase/app';

export function hasNotesListIntegrationAccess(user) {
    if (!NOTESLIST_INTEGRATION_BILLING_ENFORCED) {
        return true;
    }
    // License / seat holders inherit sync from the parent admin's plan.
    // A local dual flag on the seat holder can be stale (e.g. parent is
    // Cuelist-only) — never trust it; callers must use fetchNotesListSyncAccess.
    if (user && user.affiliateInstitution) {
        return false;
    }
    if (!user || !user.noteslist_integration_access) {
        return false;
    }
    const periodEnd = user.noteslist_integration_period_end;
    if (periodEnd) {
        const endMs = periodEnd.toDate
            ? periodEnd.toDate().getTime()
            : new Date(periodEnd).getTime();
        if (Number.isFinite(endMs) && endMs < Date.now()) {
            return false;
        }
    }
    return true;
}

/**
 * Ask the backend (licence GET + local dual) whether sync is allowed.
 * Falls back to local dual flag if the request fails.
 */
export async function fetchNotesListSyncAccess(projectId = null) {
    if (!NOTESLIST_INTEGRATION_BILLING_ENFORCED) {
        return { allowed: true, billingEnforced: false, via: 'billing_not_enforced' };
    }
    try {
        const user = firebase.auth().currentUser;
        if (!user) {
            return { allowed: false, code: 'NOT_AUTHENTICATED' };
        }
        const token = await user.getIdToken();
        const url = projectId
            ? `${NOTESLIST_SYNC_ACCESS_URL}?projectId=${encodeURIComponent(projectId)}`
            : NOTESLIST_SYNC_ACCESS_URL;
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            return {
                allowed: false,
                code: body.code || 'SYNC_ACCESS_ERROR',
                error: body.error,
            };
        }
        return await res.json();
    } catch (err) {
        return { allowed: false, code: 'SYNC_ACCESS_NETWORK', error: err.message };
    }
}

/**
 * Gate for the initial project ↔ production LINK action (Connect / Create
 * production). Distinct from fetchNotesListSyncAccess: no active plan on
 * either side blocks a NEW connection outright, while an already-linked
 * project keeps syncing/displaying as-is until unlinked.
 */
export async function fetchNotesListLinkAccess() {
    if (!NOTESLIST_INTEGRATION_BILLING_ENFORCED) {
        return { allowed: true, billingEnforced: false, via: 'billing_not_enforced' };
    }
    try {
        const user = firebase.auth().currentUser;
        if (!user) {
            return { allowed: false, code: 'NOT_AUTHENTICATED' };
        }
        const token = await user.getIdToken();
        const res = await fetch(NOTESLIST_LINK_ACCESS_URL, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            return {
                allowed: false,
                code: body.code || 'LINK_ACCESS_ERROR',
                error: body.error,
            };
        }
        return await res.json();
    } catch (err) {
        return { allowed: false, code: 'LINK_ACCESS_NETWORK', error: err.message };
    }
}

export async function fetchBundleEligibility() {
    try {
        const user = firebase.auth().currentUser;
        if (!user) {
            return {
                linked: false,
                licensed: false,
                bundleDisabled: true,
                reason: 'not_authenticated',
            };
        }
        const token = await user.getIdToken();
        const res = await fetch(NOTESLIST_BUNDLE_ELIGIBILITY_URL, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            return {
                linked: false,
                licensed: false,
                bundleDisabled: true,
                reason: 'eligibility_error',
            };
        }
        return await res.json();
    } catch (err) {
        return {
            linked: false,
            licensed: false,
            bundleDisabled: true,
            reason: 'eligibility_network',
            error: err.message,
        };
    }
}

export function notesListIntegrationUpgradeMessage(reason = null) {
    // Real subscriptions on both sides, accounts just never connected — the
    // fix is linking accounts, not buying a plan. Telling an already-paying
    // customer to buy something they already have is its own bug (see the
    // Add Note gate audit, row 4).
    switch (reason) {
    case 'not_linked':
        return "Your Cuelist and Notes List accounts aren't linked yet. Connect them in Account Settings to sync.";
    case 'no_subscription':
        return 'You need an active Cuelist subscription to sync cue and script data. Manage your plan in Account Settings.';
    case 'not_licensed_on_noteslist':
        return 'You need an active Notes List subscription to sync cue and script data. Manage your plan in Account Settings.';
    default:
        return 'You need an active plan on both Cuelist and Notes List (or Cuelist + Notes List dual) to sync cue and script data. Manage your plan in Account Settings.';
    }
}

/** Module 9 — collaborators cannot manage project ↔ production link or re-sync. */
export function notesListCollaboratorOwnerOnlyMessage() {
    return 'Only the project owner can connect this project to The Notes List or re-sync. Invite team members separately in The Notes List if they need notes access.';
}



// WEBPACK FOOTER //
// ./src/lib/noteslist-integration-access.js